/**
 * Members Management Page
 * 
 * Admin-only page for managing family members:
 * - View all members
 * - Change roles (Admin/Member)
 * - Remove members
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyAuth } from '../../../contexts/FamilyAuthContext';
import axiosClient from '../../../api/axiosClient';

function MembersManagement() {
  const { familyUser, household, isAdmin } = useFamilyAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingMemberId, setDeletingMemberId] = useState(null);
  const [changingRoleId, setChangingRoleId] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/family/dashboard');
      return;
    }
    fetchMembers();
  }, [isAdmin, navigate]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await axiosClient.get('/api/admin/members');
      setMembers(response.data.members || []);
      setError('');
    } catch (err) {
      console.error('Failed to fetch members:', err);
      setError('Fehler beim Laden der Mitglieder');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (memberId, newRole) => {
    if (!window.confirm(`Rolle wirklich zu "${newRole}" ändern?`)) {
      return;
    }

    try {
      setChangingRoleId(memberId);
      await axiosClient.put(`/api/admin/members/${memberId}/role?new_role=${newRole}`);
      
      // Update local state
      setMembers(prev => prev.map(m => 
        m.id === memberId ? { ...m, role: newRole } : m
      ));
      
      alert('Rolle erfolgreich geändert');
    } catch (err) {
      console.error('Failed to change role:', err);
      alert('Fehler beim Ändern der Rolle: ' + (err.response?.data?.detail || err.message));
    } finally {
      setChangingRoleId(null);
    }
  };

  const handleDeleteMember = async (memberId, memberName) => {
    if (!window.confirm(`Mitglied "${memberName}" wirklich entfernen?`)) {
      return;
    }

    try {
      setDeletingMemberId(memberId);
      await axiosClient.delete(`/api/admin/members/${memberId}`);
      
      // Remove from local state
      setMembers(prev => prev.filter(m => m.id !== memberId));
      
      alert('Mitglied erfolgreich entfernt');
    } catch (err) {
      console.error('Failed to delete member:', err);
      alert('Fehler beim Entfernen: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingMemberId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050816] text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-[#5B4B8A] border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-neutral-400">Lade Mitglieder...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      {/* Header */}
      <div className="bg-[#0D1124]/90 backdrop-blur-xl border-b border-neutral-600/40 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/family/dashboard')}
              className="p-2 hover:bg-neutral-700/50 rounded-lg transition-colors"
            >
              ← Zurück
            </button>
            <div>
              <h1 className="text-2xl font-bold">Mitgliederverwaltung</h1>
              <p className="text-sm text-neutral-400">
                Verwalte Rollen und Berechtigungen
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto p-6">
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/40 rounded-xl text-red-200">
            {error}
          </div>
        )}

        {/* Members List */}
        <div className="bg-[#0D1124]/80 backdrop-blur-xl rounded-2xl border border-neutral-600/40 overflow-hidden">
          <div className="p-6 border-b border-neutral-600/40">
            <h2 className="text-xl font-semibold">
              Mitglieder ({members.length})
            </h2>
          </div>

          <div className="divide-y divide-neutral-600/40">
            {members.map((member) => (
              <div 
                key={member.id}
                className="p-6 hover:bg-neutral-700/20 transition-colors"
              >
                <div className="flex items-center justify-between gap-4">
                  {/* Member Info */}
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#5B4B8A] to-[#4a3d72] flex items-center justify-center text-white text-lg font-bold">
                      {member.name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{member.name}</h3>
                      <p className="text-sm text-neutral-400">{member.email}</p>
                      {member.id === familyUser?.id && (
                        <span className="text-xs text-[#6DCEE8]">(Du)</span>
                      )}
                    </div>
                  </div>

                  {/* Role & Actions */}
                  <div className="flex items-center gap-3">
                    {/* Role Dropdown */}
                    <select
                      value={member.role || 'member'}
                      onChange={(e) => handleRoleChange(member.id, e.target.value)}
                      disabled={changingRoleId === member.id || member.id === familyUser?.id}
                      className="px-4 py-2 bg-[#0D1124] border border-neutral-600/40 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#5B4B8A] disabled:opacity-50"
                    >
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                    </select>

                    {/* Delete Button */}
                    <button
                      onClick={() => handleDeleteMember(member.id, member.name)}
                      disabled={deletingMemberId === member.id || member.id === familyUser?.id}
                      className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deletingMemberId === member.id ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-red-200 border-t-transparent rounded-full animate-spin"></div>
                          Entfernen...
                        </span>
                      ) : (
                        'Entfernen'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Invite Button */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/family/admin/invite')}
            className="px-6 py-3 bg-gradient-to-r from-[#5B4B8A] to-[#6DCEE8] hover:opacity-90 rounded-xl font-semibold transition-all"
          >
            + Neues Mitglied einladen
          </button>
        </div>
      </div>
    </div>
  );
}

export default MembersManagement;
